import { directionalProperty } from 'polished';
import styled, { css } from 'styled-components';

import { ElementSize } from '../../../../types';
import { subtractUnit } from '../../../../utils/styles';
import { INPUT_BORDER_WIDTH_PX } from '../../constants';
import getInputBorderStyles from '../../utils/getInputBorderStyles';
import getInputIconStyles from '../../utils/getInputIconStyles';
import getThemeInputVariant from '../../utils/getThemeInputVariant';

interface InputMainWrapperRootProps {
  $hasError: boolean;
  $isDisabled?: boolean;
  $size: ElementSize;
  $hasNoDisabledStyle: boolean | undefined;
}

export const InputMainWrapperRoot = styled.div<InputMainWrapperRootProps>`
  ${({ theme, $size, $isDisabled, $hasError, $hasNoDisabledStyle }) => {
    const variant = getThemeInputVariant(theme, $size);

    const verticalPadding = subtractUnit(
      variant['vertical-padding'],
      INPUT_BORDER_WIDTH_PX,
    );

    const horizontalPadding = subtractUnit(
      variant['horizontal-padding'],
      INPUT_BORDER_WIDTH_PX,
    );

    const borderStyles = getInputBorderStyles({
      hasError: $hasError,
      isDisabled: $isDisabled,
    });

    return css`
      box-sizing: border-box;
      position: relative;
      display: flex;
      align-items: center;
      overflow: hidden;
      transition: all 0.1s ease-in-out;

      gap: ${variant.inside};

      ${directionalProperty(
        'padding',
        verticalPadding || 0,
        horizontalPadding || 0,
      )}

      border-radius: ${variant.radius};
      background-color: ${$isDisabled && !$hasNoDisabledStyle
        ? variant['bg-disabled']
        : variant['bg-default']};

      color: ${$isDisabled && !$hasNoDisabledStyle
        ? variant['fg-disabled']
        : variant['fg-default']};

      ${borderStyles}

      > svg {
        ${getInputIconStyles(theme, $isDisabled)}
      }

      /**
      Some browsers (like Chrome) override the input background color on autofill to something custom
      (ex. chrome is blueish, safari is yellow, etc.), and they do it in way that can't be overriden.
      So this is solution for this so we can have control over how it looks when input gets autofilled
      @see - https://stackoverflow.com/questions/2781549/removing-input-background-colour-for-chrome-autocomplete
      */
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active {
        background-clip: text;
        -webkit-text-fill-color: ${variant['fg-default']};
        transition: background-color 5000s ease-in-out 0s;
        box-shadow: inset 0 0 20px 20px ${variant['bg-default']};
      }
    `;
  }}
`;

export const InputMainWrapperSideArea = styled.div<{
  $isDisabled: boolean | undefined;
  $size: ElementSize;
}>`
  ${({ theme, $size, $isDisabled }) => {
    const variant = getThemeInputVariant(theme, $size);

    return css`
      display: flex;
      align-items: center;
      flex-grow: 0;
      flex-shrink: 0;
      gap: ${variant.inside};

      > svg {
        ${getInputIconStyles(theme, $isDisabled)}
      }

      &:empty {
        display: none;
      }
    `;
  }}
`;
